/**
 * @desc: 年报页面 - iframe 嵌入第三方年报内容
 */
import React, { useRef, useEffect, useState, useCallback } from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import { Layout } from 'tea-component';
import { getYearReportUrl, getLanguageParam } from '@user-center/_config/yearReport';
import { TIPS_VIEW_STATE_KEY_MAP } from '@user-center/_config/tipsViewState';
import useTipsView from '@user-center/_hooks/useTipsView';

import './index.scss';

const { Body, Content } = Layout;

interface IYearReportProps extends IBaseProps {}

const YearReport: React.FC<IYearReportProps> = (props) => {
  const { style, className } = props;

  const { locale, formatMessage } = useIntl();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeSrc, setIframeSrc] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // 使用 useTipsView Hook 来管理访问状态
  const { tipsViewLoading, isTipsView: hasVisited, handleViewedTips } = useTipsView(TIPS_VIEW_STATE_KEY_MAP.YEAR_REPORT_PAGE);

  // 自动收起左侧菜单的函数
  const autoCollapseLeftMenu = useCallback(() => {
    // 检查左侧菜单是否存在且为展开状态
    const menuContainer = document.querySelector('.db-left-menu-container');
    if (menuContainer && menuContainer.classList.contains('normal-with')) {
      // 找到收起按钮并模拟点击
      const foldButton = document.querySelector('.navmenu-ft-right');
      if (foldButton) {
        (foldButton as HTMLElement).click();
      }
    }
  }, []);

  // 页面初始化时自动收起左侧菜单
  useEffect(() => {
    autoCollapseLeftMenu();
  }, [autoCollapseLeftMenu]);

  // 初始化 iframe URL
  useEffect(() => {
    setIframeSrc(getYearReportUrl(locale));
  }, []);

  // 记录访问状态，只有未访问过才上报，用于隐藏 NEW 标签
  useEffect(() => {
    if (tipsViewLoading){
        return;
    }
    if (!hasVisited) {
      handleViewedTips();
    }
  }, [hasVisited, tipsViewLoading]);

  // 语言切换时发送 postMessage 到 iframe
  useEffect(() => {
    const iframe = iframeRef.current;
    if (iframe?.contentWindow && iframeSrc) {
      iframe.contentWindow.postMessage(
        { type: 'LANGUAGE_CHANGE', language: getLanguageParam(locale) },
        '*',
      );
    }
  }, [locale, iframeSrc]);

  // iframe 加载完成回调
  const handleIframeLoad = useCallback(() => {
    setIsLoading(false);
    setHasError(false);
  }, []);

  // iframe 加载错误回调
  const handleIframeError = useCallback(() => {
    setIsLoading(false);
    setHasError(true);
  }, []);

  return (
    <Layout style={style} className={classnames('year-report-layout', className)}>
      <Content className="year-report-content">
        <Content.Header title={formatMessage({ id: 'dbu_year_report_title' })}>
        </Content.Header>
        {isLoading && (
          <div className="year-report-loading">
            {formatMessage({ id: 'dbu_year_report_loading' })}
          </div>
        )}
        {hasError && (
          <div className="year-report-error">
            {formatMessage({ id: 'dbu_year_report_load_error' })}
          </div>
        )}
        {iframeSrc && (
          <iframe
            ref={iframeRef}
            className="year-report-iframe"
            src={iframeSrc}
            title={formatMessage({ id: 'dbu_year_report_title' })}
            onLoad={handleIframeLoad}
            onError={handleIframeError}
          />
        )}
      </Content>
    </Layout>
  );
};

export default YearReport;
